import React, { useEffect, useRef, useState } from "react"
import { Card, CardBody, CardTitle, Col, Container, Row } from "reactstrap"

//i18n
import { useTranslation, withTranslation } from "react-i18next"
import { dountChartBgColor, dountChartHoverBgColor } from "./dashboardColors"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { getDashboardData } from "store/actions/dashboardAction"
import Spinners from "components/Shared/Spinners"
import {
  clearDashboardCache,
  getDropdownData,
} from "store/actions/commonAction"
import { showAlert } from "store/reducers/alertReducer"
import { formatDateForAPI } from "components/Common/Utils"

import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import DashboardFilter from "./DashboardFilter"
import RecruitmentStatistics from "./RecruitmentStatistics"
import Switch from "components/Shared/Switch"
import IconifyIcon from "components/Shared/IconifyIcon"
import moment from "moment"
import GeneralstatisticsV2 from "./GeneralStatistics/GeneralstatisticsV2"

import { layoutModeTypes, leftSideBarThemeTypes } from "constants/layout"
import {
  changeLayoutMode,
  changeSidebarTheme,
} from "../../store/layout/actions"
import Colors from "constants/Colors"
import TheoreticalTrainingStatisticsV2 from "./TheoreticalTraining/TheoreticalTrainingStatisticsV2"
import TrialOperationsActualStatisticsV2 from "./TrialOperationStatistics/TrialOperationsActualStatisticsV2"
import TrialOperationsPresentStatisticsV2 from "./TrialOperationStatistics/TrialOperationsPresentStatisticsV2"
import PracticalTrainingStatistics from "./PracticalTraining/PracticalTrainingStatistics"

const Dashboard = props => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Dashboard | Yamama")

  const dispatch = useDispatch()
  const pdfRef = useRef()
  const { loading, isLoading, dashboardData } = useSelector(
    state => state.Dashboard
  )

  const [isExporting, setIsExporting] = useState(false)
  const [generalStatistic, setGeneralStatistic] = useState("general_statistics")
  const [showRealTimeStatistics, setShowRealTimeStatistics] = useState(false)
  const layoutMode = useSelector(state => state.Layout.layoutModeType)
  const [isLightTheme, setIsLightTheme] = useState(
    layoutMode === layoutModeTypes.LIGHT
  )

  const [currentTime, setCurrentTime] = useState(new Date())

  const [filters, setFilters] = useState({
    generalStatistic: generalStatistic,
    is_saudi: null,
    dateType: "today",
    city_ids: [],
    gender: null,
    ageGroup: { min: null, max: null },
    education_qualification_id: [],
    dateRange: { start: null, end: null },
  })

  const [isShowFilter, setIsShowFilter] = useState(false)

  // useEffect(() => {
  //   if (generalStatistic === "theoretical_training_statistics") {
  //     setFilters({ ...filters, dateType: "all" })
  //   } else {
  //     setFilters({ ...filters, dateType: "today" })
  //   }
  // }, [generalStatistic])

  useEffect(() => {
    getAllData()
  }, [showRealTimeStatistics])

  useEffect(() => {
    fetchDropdownData()
  }, [])

  const getAllData = (isReset = false) => {
    const {
      generalStatistic,
      is_saudi,
      gender,
      city_ids,
      ageGroup,
      education_qualification_id,
      dateType,
      dateRange,
    } = filters

    if (ageGroup && ageGroup.max < ageGroup.min) {
      dispatch(
        showAlert({
          text: t("Max age should be greater than or equal to Min age"),
          type: "error",
        })
      )
      return
    }

    const updatedCities = city_ids?.map(item => item?.value) || []
    const updatedEducationQualification =
      education_qualification_id?.map(item => item?.value) || []

    const payload = {
      type: generalStatistic, // general_statistics
      use_cache: !showRealTimeStatistics,
      filters: isReset
        ? {
            city_ids: [],
            is_saudi: null,
            gender: null,
            // education_qualification_id: null,
            education_qualification_ids: [],
            ageGroup: { min: null, max: null },
            dateFilter: { type: "all" },
          }
        : {
            city_ids: updatedCities,
            is_saudi, //null for all 1:Saudi,0:Non Saudi
            gender, // null for all,male or female
            education_qualification_ids: updatedEducationQualification,
            ageGroup,
            dateFilter: {
              type:
                generalStatistic === "theoretical_training_statistics" ||
                generalStatistic === "practical_training_statistics"
                  ? "all"
                  : dateType, //all,today,week,month,6_month,year or custom
              start: formatDateForAPI(dateRange?.start),
              end: formatDateForAPI(dateRange?.end),
            },
          },
    }
    dispatch(getDashboardData(payload))
    setGeneralStatistic(generalStatistic)
  }

  const fetchDropdownData = () => {
    dispatch(getDropdownData())
  }

  const handleSearch = () => {
    getAllData()
  }

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Enter") {
        // Prevent default behavior if inside a textarea
        if (e.target.tagName === "TEXTAREA") return

        e.preventDefault()
        handleSearch()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleSearch])

  const handleReset = () => {
    setFilters({
      generalStatistic: generalStatistic,
      city_ids: [],
      is_saudi: null,
      gender: null,
      ageGroup: { min: null, max: null }, // Reset to min: 0, max: 100
      // education_qualification_id: null,
      education_qualification_id: [],
      dateType: "today",
      dateRange: { start: null, end: null },
    })
    getAllData(true) // isReset
  }

  const getChartData = (stats, labelKey) => ({
    labels:
      stats?.map(item =>
        language === "sa" ? item[`${labelKey}_ar`] : item[labelKey]
      ) || [],
    datasets: [
      {
        data: stats?.map(item => item.count) || [],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  })

  const nationalityChartData = getChartData(
    dashboardData?.nationalities_stats,
    "nationality_name"
  )
  const citiesChartData = getChartData(dashboardData?.city_stats, "city_name")
  const educationChartData = getChartData(
    dashboardData?.education_stats,
    "education_name"
  )

  const genderChartData = {
    labels: [t("Male"), t("Female")], // Labels for the chart
    datasets: [
      {
        data: [
          dashboardData?.male_or_female_stats?.male_count || 0,
          dashboardData?.male_or_female_stats?.female_count || 0,
        ],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  }

  const chartConfigs = [
    { title: "Nationalities", data: nationalityChartData },
    { title: "Cities", data: citiesChartData },
    { title: "Education Qualification", data: educationChartData },
    { title: "Gender", data: genderChartData },
  ]

  const exportAsPDF = async () => {
    setIsExporting(true)

    try {
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const margin = 10 // margin in mm
      const contentWidth = pageWidth - margin * 2

      // Get all the page sections
      const pageSections = pdfRef.current.querySelectorAll(
        '[data-page-section="true"]'
      )

      // For each section
      for (let i = 0; i < pageSections.length; i++) {
        const section = pageSections[i]

        // Add new page if not the first section
        if (i > 0) {
          pdf.addPage()
        }

        // Convert this section to canvas
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        })

        const imgData = canvas.toDataURL("image/png")

        // Calculate dimensions while preserving aspect ratio
        const imgWidth = contentWidth
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        // If this section fits on one page
        if (imgHeight <= pageHeight - margin * 2) {
          // Add image centered on the page
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
        }
        // If this section needs multiple pages
        else {
          let heightLeft = imgHeight
          let position = 0
          let pageCount = 0

          // Add first page of this section
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
          heightLeft -= pageHeight - margin * 2

          // Add additional pages as needed for this section
          while (heightLeft > 0) {
            pageCount++
            position = -pageHeight * pageCount + margin // Position adjustment
            pdf.addPage()
            pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight)
            heightLeft -= pageHeight - margin * 2
          }
        }
      }

      pdf.save("DashboardReport.pdf")
    } catch (error) {
      console.error("PDF export failed:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const data = {
    labels: dashboardData?.ages_stats?.map(a => a?.age) || [],
    datasets: [
      {
        label: t("Total Count"),
        data: dashboardData?.ages_stats?.map(a => a.count) || [],
        backgroundColor: "#00778B",
      },
    ],
  }

  const options = {
    indexAxis: "y",
    plugins: {
      datalabels: {
        anchor: "end",
        align: "end",
        color: "#fff",
      },
      legend: {
        onClick: null,
        labels: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },

    responsive: true,
    scales: {
      x: {
        grid: {
          color: "#f1f1f1",
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
      y: {
        grid: {
          color: "#f1f1f1",
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },
  }

  return (
    <React.Fragment>
      <div className="page-content dashboard_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Card>
              <CardBody>
                <Row>
                  <Col md={6} sm={12}>
                    <div>
                      <CardTitle
                        style={{
                          color:
                            layoutMode === layoutModeTypes.LIGHT
                              ? Colors.primary
                              : "white",
                        }}
                      >
                        {t("Dashboards")}
                      </CardTitle>
                      <div
                        style={{
                          color:
                            layoutMode === layoutModeTypes.LIGHT
                              ? Colors.primary
                              : Colors.primaryLight,
                        }}
                      >
                        <IconifyIcon
                          icon="solar:refresh-line-duotone"
                          width={19}
                          height={19}
                          style={{
                            color:
                              layoutMode === layoutModeTypes.LIGHT
                                ? Colors.primary
                                : Colors.primaryLight,
                          }}
                          onClick={() => {
                            dispatch(clearDashboardCache())
                            getAllData()
                            setCurrentTime(new Date())
                          }}
                        />
                        <span>
                          &nbsp;{t("Last updated at")}&nbsp;
                          {moment(currentTime).format("DD/M/YYYY H:mm")}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col
                    md={6}
                    sm={12}
                    className="d-flex mt-2  justify-content-end align-items-center"
                  >
                    <div className="d-flex gap-2">
                      <Switch
                        onValue={t("Real time")}
                        offValue={t("Real time")}
                        isChecked={showRealTimeStatistics}
                        onClickHandler={e => console.log("asdasd", e)}
                        onChange={e => {
                          setShowRealTimeStatistics(e.target.checked)
                          if (e.target.checked) dispatch(clearDashboardCache())
                        }}
                        layoutMode={layoutMode}
                      />

                      <IconifyIcon
                        icon={isLightTheme ? "ri:moon-fill" : "solar:sun-bold"}
                        className={isLightTheme ? "text-primary" : "text-white"}
                        onClick={() => {
                          // dispatch(
                          //   changeSidebarTheme(
                          //     isLightTheme
                          //       ? leftSideBarThemeTypes.DARK
                          //       : leftSideBarThemeTypes.LIGHT
                          //   )
                          // )
                          dispatch(
                            changeLayoutMode(
                              isLightTheme
                                ? layoutModeTypes.DARK
                                : layoutModeTypes.LIGHT
                            )
                          )
                          setIsLightTheme(!isLightTheme)
                        }}
                      />
                      {/* <IconifyIcon
                      icon="bi:filetype-xls"
                      className={isLightTheme ? "text-primary" : "text-white"}
                      onClick={() => {
                        setIsShowFilter(!isShowFilter)
                      }}
                    /> */}
                      <IconifyIcon
                        icon="solar:file-download-linear"
                        className={isLightTheme ? "text-primary" : "text-white"}
                        onClick={exportAsPDF}
                      />
                      <IconifyIcon
                        icon={
                          isShowFilter
                            ? "hugeicons:filter-remove"
                            : "hugeicons:filter-reset"
                        }
                        className={isLightTheme ? "text-primary" : "text-white"}
                        onClick={() => {
                          setIsShowFilter(!isShowFilter)
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <div className="g_between"></div>

                {isShowFilter && (
                  <DashboardFilter
                    filters={filters}
                    setFilters={setFilters}
                    handleSearch={handleSearch}
                    handleReset={handleReset}
                    currentDashboard={generalStatistic}
                  />
                )}
              </CardBody>
            </Card>
            <div ref={pdfRef}>
              {/* GENERAL STATISTICS */}
              {/* {generalStatistic === "general_statistics" && (
                <>
                  <Row data-page-section="true">
                    <Col lg={8}>
                      <ReportsDashboard dashboardData={dashboardData} />
                    </Col>
                    <Col lg={4}>
                      <LanguagesDashboard dashboardData={dashboardData} />
                    </Col>
                  </Row>
                  <Row data-page-section="true">
                    {chartConfigs?.map(({ title, data }) => (
                      <Col xl={6} key={title}>
                        <Card>
                          <CardBody>
                            <CardTitle>{t(title)}</CardTitle>
                            {hasValidChartData(data) ? (
                              <div className="ltr">
                                <DountChart
                                  data={data}
                                  options={dountChartOptions}
                                />
                              </div>
                            ) : (
                              <div className="g_center h-75">
                                <h5>{t("No Data Available")}</h5>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    ))}

                    <Col xl={12}>
                      <Card>
                        <CardBody>
                          <CardTitle>{t("Age")}</CardTitle>
                          <div className="ltr">
                            <BarChart
                              data={data}
                              options={options}
                              height={300}
                            />
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </>
              )} */}
              {generalStatistic === "general_statistics" && (
                <GeneralstatisticsV2
                  chartConfigs={chartConfigs}
                  data={data}
                  options={options}
                  dashboardData={dashboardData}
                />
              )}

              {generalStatistic === "interview_statistics" && ( // Recruitment Statistics
                <>
                  <div className="recruitment_statistics">
                    <RecruitmentStatistics filters={filters} />
                  </div>
                </>
              )}
              {/* {generalStatistic === "theoretical_training_statistics" && (
                <>
                  <div className="theoreticalTrainingStatistics">
                    <TheoreticalTrainingStatistics filters={filters} />
                  </div>
                </>
              )} */}
              {generalStatistic === "theoretical_training_statistics" && (
                <Card>
                  <CardBody>
                    <div className="theoreticalTrainingStatistics">
                      <TheoreticalTrainingStatisticsV2 filters={filters} />
                    </div>
                  </CardBody>
                </Card>
              )}
              {generalStatistic === "practical_training_statistics" && (
                <Card>
                  <CardBody>
                    <div className="practical_training_statistics">
                      <PracticalTrainingStatistics filters={filters} />
                    </div>
                  </CardBody>
                </Card>
              )}
              {/* {generalStatistic === "trial_run_statistics" && (
                <>
                  <div className="trialOperationsStatistics">
                    <TrialOperationsActualStatistics filters={filters} />
                  </div>
                </>
              )} */}
              {generalStatistic === "trial_run_statistics" && (
                <>
                  <div className="trialOperationsStatistics">
                    <TrialOperationsActualStatisticsV2 filters={filters} />
                  </div>
                </>
              )}
              {/* {generalStatistic === "trial_run_present_statistics" && (
                <>
                  <div className="trialOperationsPresentStatistics">
                    <TrialOperationsPresentStatistics filters={filters} />
                  </div>
                </>
              )} */}
              {generalStatistic === "trial_run_present_statistics" && (
                <>
                  <div className="trialOperationsStatistics">
                    <TrialOperationsPresentStatisticsV2 filters={filters} />
                  </div>
                </>
              )}
            </div>
          </Container>
        )}
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(Dashboard)
